import React, { useState, useEffect } from "react";
import { db, auth } from "../firebaseConfig";
import { doc, getDoc, collection, addDoc, getDocs } from "firebase/firestore";
import { Button, Row, Col, Card, Form, ProgressBar, Container, Alert, FormControl } from "react-bootstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import Header from "../layouts/Header";

export default function FileManager() {
  const [files, setFiles] = useState([]);
  const [file, setFile] = useState(null);
  const [mediaLinks, setMediaLinks] = useState([]);
  const [mediaLink, setMediaLink] = useState("");
  const [mediaName, setMediaName] = useState(""); // Added media name
  const [mediaType, setMediaType] = useState("photo"); // photo or video
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [createdForEmail, setCreatedForEmail] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchUserData = async () => {
    const user = auth.currentUser;
    if (user) {
      setEmail(user.email || "No email");
      const userDocRef = doc(db, "Users", user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setFullName(userData.fullName || "Unknown User");
        setRole(userData.role || "user");
      } else {
        console.error("User document does not exist");
      }
    }
  };

  const fetchAllUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, "Users"));
      const userList = usersSnapshot.docs.map((doc) => ({
        email: doc.data().email,
        fullName: doc.data().fullName || "Unknown User",
      }));
      setUsers(userList);
      setFilteredUsers(userList);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  useEffect(() => {
    fetchUserData();
    fetchAllUsers();
  }, []);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) return alert("Please select a file first.");
    if (!createdForEmail) return alert("Please enter the email of the user this file is for.");
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "documents");

      const xhr = new XMLHttpRequest();
      xhr.open("POST", "https://api.cloudinary.com/v1_1/dwcbtliac/upload");

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      };

      xhr.onload = async () => {
        const data = JSON.parse(xhr.responseText);
        const fileRef = collection(db, "Files");
        await addDoc(fileRef, {
          fileName: file.name,
          url: data.secure_url,
          size: file.size,
          type: file.type,
          uploadedAt: new Date(),
          createdBy: { email, fullName },
          createdFor: createdForEmail,
        });

        setUploading(false);
        setUploadProgress(0);
        setFile(null);
        setCreatedForEmail("");
        alert("File uploaded successfully!");
        fetchFiles();
      };

      xhr.onerror = () => {
        setUploading(false);
        setUploadProgress(0);
        alert("Failed to upload file.");
      };

      xhr.send(formData);
    } catch (err) {
      console.error(err);
      alert("Failed to upload file.");
    }
  };

  const handleMediaLinkAdd = async () => {
    if (!mediaLink || !mediaName || !createdForEmail) {
      alert("Please enter a valid link, name, and recipient's email.");
      return;
    }
    try {
      const mediaRef = collection(db, "MediaLinks");
      await addDoc(mediaRef, {
        link: mediaLink,
        name: mediaName,
        type: mediaType,
        createdAt: new Date(),
        createdBy: { email, fullName },
        createdFor: createdForEmail,
      });

      setMediaLink("");
      setMediaName("");
      setMediaType("photo");
      setCreatedForEmail("");
      alert("Media link added successfully!");
      fetchMediaLinks();
    } catch (err) {
      console.error(err);
      alert("Failed to add media link.");
    }
  };

  const fetchFiles = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      const fileSnapshot = await getDocs(collection(db, "Files"));
      const allFiles = fileSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      let filteredFiles = [];
      if (role === "admin") {
        filteredFiles = selectedUser
          ? allFiles.filter(
              (file) =>
                file.createdBy.email === selectedUser.email ||
                file.createdFor === selectedUser.email
            )
          : allFiles;
      } else {
        filteredFiles = allFiles.filter(
          (file) => file.createdBy.email === email || file.createdFor === email
        );
      }

      setFiles(filteredFiles);
    } catch (err) {
      console.error(err);
      alert("Failed to fetch files.");
    }
  };

  const fetchMediaLinks = async () => {
    try {
      const mediaSnapshot = await getDocs(collection(db, "MediaLinks"));
      const allMediaLinks = mediaSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      let filteredMediaLinks = [];
      if (role === "admin") {
        filteredMediaLinks = selectedUser
          ? allMediaLinks.filter(
              (media) =>
                media.createdBy.email === selectedUser.email ||
                media.createdFor === selectedUser.email
            )
          : allMediaLinks;
      } else {
        filteredMediaLinks = allMediaLinks.filter(
          (media) => media.createdBy.email === email || media.createdFor === email
        );
      }
      setMediaLinks(filteredMediaLinks);
    } catch (err) {
      console.error(err);
      alert("Failed to fetch media links.");
    }
  };

  useEffect(() => {
    fetchFiles();
    fetchMediaLinks();
  }, [role, selectedUser]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    const lowerCaseQuery = query.toLowerCase();
    const filtered = users.filter(
      (user) =>
        user.fullName.toLowerCase().includes(lowerCaseQuery) ||
        user.email.toLowerCase().includes(lowerCaseQuery)
    );
    setFilteredUsers(filtered);
  };

  /**
   * Extracts an embeddable video link from a given URL (e.g., YouTube, Vimeo).
   */
  const getEmbeddableVideoLink = (url) => {
    const youtubeMatch = url.match(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    const vimeoMatch = url.match(/(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/);

    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    return null;
  };

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* Enhanced Header Section */}
        <div className="text-center mb-5">
          <div className="d-flex align-items-center justify-content-center mb-3">
            <i className="ri-folder-line fs-1 text-primary me-3"></i>
            <h1 className="display-6 fw-bold text-primary mb-0">File Manager</h1>
          </div>
          <p className="lead text-muted">Upload, manage, and share your files and media</p>
        </div>

        {/* Enhanced Alert Cards */}
        <Row className="mb-5">
          <Col xs={12} lg={6}>
            <div className="bg-gradient-primary text-white rounded-4 p-4 shadow-lg mb-3"
                 style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <div className="d-flex align-items-center">
                <i className="ri-information-line fs-4 me-3"></i>
                <div>
                  <h6 className="mb-1 fw-bold">User Role</h6>
                  <p className="mb-0 small">
                    {role === "admin"
                      ? "You are logged in as an admin and can view all files and media for all users."
                      : `You are logged in as a ${role}. You can view files and media created by you or for you.`}
                  </p>
                </div>
              </div>
            </div>
          </Col>
          <Col xs={12} lg={6}>
            <div className="bg-gradient-warning text-white rounded-4 p-4 shadow-lg mb-3"
                 style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
              <div className="d-flex align-items-center">
                <i className="ri-alert-line fs-4 me-3"></i>
                <div>
                  <h6 className="mb-1 fw-bold">Media Recommendation</h6>
                  <p className="mb-0 small">
                    For media (photos or videos), it is advised to provide direct links instead of uploading files.
                  </p>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Enhanced User Filter Card */}
        {role === "admin" && (
          <Card className="border-0 shadow-lg rounded-4 mb-5">
            <div className="bg-gradient-info text-white p-4 rounded-top-4" 
                 style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
              <div className="d-flex align-items-center">
                <i className="ri-user-search-line fs-4 me-3"></i>
                <h5 className="mb-0 fw-bold">Filter Files by User</h5>
              </div>
            </div>
            <Card.Body className="p-4">
              <FormControl
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="mb-3 form-control-lg shadow-sm border-2"
                style={{ borderRadius: '12px' }}
              />
              <PerfectScrollbar style={{ maxHeight: "200px", overflow: "auto" }}>
                <div className="row g-2">
                  {filteredUsers.map((user, index) => (
                    <div key={index} className="col-12">
                      <div
                        className={`p-3 rounded-3 cursor-pointer ${selectedUser === user ? "bg-primary text-white" : "bg-light"}`}
                        onClick={() => setSelectedUser(user)}
                        style={{ 
                          cursor: "pointer",
                          transition: 'all 0.3s ease',
                          border: selectedUser === user ? '2px solid #0d6efd' : '2px solid transparent'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedUser !== user) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedUser !== user) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <div className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${selectedUser === user ? 'bg-white text-primary' : 'bg-primary text-white'}`}
                               style={{ width: '40px', height: '40px' }}>
                            <i className="ri-user-line"></i>
                          </div>
                          <div>
                            <h6 className="mb-0 fw-semibold">{user.fullName}</h6>
                            <small className={selectedUser === user ? 'text-white-50' : 'text-muted'}>{user.email}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </PerfectScrollbar>
            </Card.Body>
          </Card>
        )}

        {/* Enhanced Upload Cards */}
        <Row className="g-4 mb-5">
          <Col md="6">
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="bg-gradient-success text-white p-4" 
                   style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-upload-cloud-2-line fs-4 me-3"></i>
                  <h4 className="mb-0 fw-bold">Upload File</h4>
                </div>
              </div>
              <Card.Body className="p-4">
                <Form.Group controlId="fileUpload">
                  <Form.Control
                    type="file"
                    onChange={handleFileChange}
                    className="mb-3 form-control-lg shadow-sm border-2"
                    style={{ borderRadius: '12px' }}
                    placeholder="Select a file"
                  />
                  <Form.Control
                    type="email"
                    value={createdForEmail}
                    onChange={(e) => setCreatedForEmail(e.target.value)}
                    placeholder="Enter recipient's email"
                    className="mb-3 form-control-lg shadow-sm border-2"
                    style={{ borderRadius: '12px' }}
                  />
                  <Button 
                    variant="success" 
                    onClick={handleUpload} 
                    disabled={uploading}
                    className="w-100 py-3 rounded-pill fw-semibold shadow-lg"
                    style={{ fontSize: '1.1rem' }}
                  >
                    {uploading ? (
                      <>
                        <i className="ri-loader-4-line me-2"></i>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="ri-upload-cloud-2-line me-2"></i>
                        Upload File
                      </>
                    )}
                  </Button>
                  {uploading && (
                    <div className="mt-3">
                      <div className="d-flex justify-content-between mb-1">
                        <small className="text-muted">Upload Progress</small>
                        <small className="text-muted fw-semibold">{uploadProgress}%</small>
                      </div>
                      <ProgressBar
                        animated
                        now={uploadProgress}
                        className="shadow-sm"
                        style={{ height: '8px', borderRadius: '10px' }}
                      />
                    </div>
                  )}
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6">
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="bg-gradient-info text-white p-4" 
                   style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-links-line fs-4 me-3"></i>
                  <h4 className="mb-0 fw-bold">Add Media Link</h4>
                </div>
              </div>
              <Card.Body className="p-4">
                <Form.Group controlId="mediaLinkUpload">
                  <Form.Control
                    type="text"
                    value={mediaName}
                    onChange={(e) => setMediaName(e.target.value)}
                    placeholder="Enter media name"
                    className="mb-3 form-control-lg shadow-sm border-2"
                    style={{ borderRadius: '12px' }}
                  />
                  <Form.Control
                    type="text"
                    value={mediaLink}
                    onChange={(e) => setMediaLink(e.target.value)}
                    placeholder="Enter photo or video link"
                    className="mb-3 form-control-lg shadow-sm border-2"
                    style={{ borderRadius: '12px' }}
                  />
                  <Form.Select
                    value={mediaType}
                    onChange={(e) => setMediaType(e.target.value)}
                    className="mb-3 form-control-lg shadow-sm border-2"
                    style={{ borderRadius: '12px' }}
                  >
                    <option value="photo">📷 Photo</option>
                    <option value="video">🎥 Video</option>
                  </Form.Select>
                  <Form.Control
                    type="email"
                    value={createdForEmail}
                    onChange={(e) => setCreatedForEmail(e.target.value)}
                    placeholder="Enter recipient's email"
                    className="mb-3 form-control-lg shadow-sm border-2"
                    style={{ borderRadius: '12px' }}
                  />
                  <Button 
                    variant="primary" 
                    onClick={handleMediaLinkAdd}
                    className="w-100 py-3 rounded-pill fw-semibold shadow-lg"
                    style={{ fontSize: '1.1rem' }}
                  >
                    <i className="ri-links-line me-2"></i>
                    Add Media Link
                  </Button>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Enhanced Files Section */}
        <Card className="border-0 shadow-lg rounded-4 mb-5">
          <div className="bg-gradient-primary text-white p-4 rounded-top-4" 
               style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className="ri-file-list-3-line fs-4 me-3"></i>
                <h4 className="mb-0 fw-bold">Uploaded Files</h4>
              </div>
              <span className="badge bg-white text-primary px-3 py-2 rounded-pill">
                {files.length} files
              </span>
            </div>
          </div>
          <Card.Body className="p-4">
            <PerfectScrollbar>
              <Row className="g-4">
                {files.map((file) => (
                  <Col xs="12" sm="6" md="4" lg="3" key={file.id}>
                    <Card className="border-0 shadow-sm rounded-4 h-100 file-card"
                          style={{ transition: 'all 0.3s ease' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                          }}>
                      <div className="bg-gradient-light p-3 text-center" 
                           style={{ background: 'linear-gradient(145deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                        <i className="ri-file-line fs-1 text-primary mb-2"></i>
                        <h6 className="text-truncate text-dark fw-semibold mb-0">{file.fileName}</h6>
                      </div>
                      <Card.Body className="p-3">
                        <div className="d-grid gap-2 mb-3">
                          <a
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-primary btn-sm rounded-pill fw-semibold"
                          >
                            <i className="ri-eye-line me-1"></i>
                            View File
                          </a>
                        </div>
                        <div className="small text-muted">
                          <div className="d-flex justify-content-between mb-1">
                            <span><i className="ri-database-line me-1"></i>Size:</span>
                            <span className="fw-semibold">{(file.size / 1024).toFixed(2)} KB</span>
                          </div>
                          <div className="d-flex justify-content-between mb-1">
                            <span><i className="ri-user-line me-1"></i>Created by:</span>
                            <span className="fw-semibold text-truncate" style={{ maxWidth: '120px' }}>
                              {file.createdBy.fullName || "Unknown"}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span><i className="ri-mail-line me-1"></i>For:</span>
                            <span className="fw-semibold text-truncate" style={{ maxWidth: '120px' }}>
                              {file.createdFor}
                            </span>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </PerfectScrollbar>
          </Card.Body>
        </Card>

        {/* Enhanced Media Section */}
        <Card className="border-0 shadow-lg rounded-4">
          <div className="bg-gradient-info text-white p-4 rounded-top-4" 
               style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className="ri-gallery-line fs-4 me-3"></i>
                <h4 className="mb-0 fw-bold">Uploaded Media</h4>
              </div>
              <span className="badge bg-white text-info px-3 py-2 rounded-pill">
                {mediaLinks.length} media
              </span>
            </div>
          </div>
          <Card.Body className="p-4">
            <PerfectScrollbar>
              <Row className="g-4">
                {mediaLinks.map((media) => (
                  <Col xs="12" sm="6" md="4" lg="3" key={media.id}>
                    <Card className="border-0 shadow-sm rounded-4 h-100 media-card"
                          style={{ transition: 'all 0.3s ease' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                          }}>
                      <div className="bg-gradient-light p-3 text-center" 
                           style={{ background: 'linear-gradient(145deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                        <i className={`fs-1 mb-2 ${media.type === 'photo' ? 'ri-image-line text-success' : 'ri-video-line text-danger'}`}></i>
                        <h6 className="text-truncate text-dark fw-semibold mb-0">{media.name}</h6>
                      </div>
                      <Card.Body className="p-3">
                        {media.type === "photo" && (
                          <div className="mb-3">
                            <img
                              src={media.link}
                              alt={media.name}
                              className="img-fluid rounded-3 shadow-sm"
                              style={{ maxHeight: '150px', objectFit: 'cover', width: '100%' }}
                            />
                          </div>
                        )}
                        {media.type === "video" && (
                          <div className="mb-3">
                            {getEmbeddableVideoLink(media.link) ? (
                              <iframe
                                width="100%"
                                height="150"
                                src={getEmbeddableVideoLink(media.link)}
                                title={media.name}
                                frameBorder="0"
                                allowFullScreen
                                className="rounded-3 shadow-sm"
                              />
                            ) : (
                              <div className="bg-light rounded-3 p-4 text-center">
                                <i className="ri-video-line fs-1 text-muted mb-2"></i>
                                <p className="small text-muted mb-2">Video Link</p>
                                <a
                                  href={media.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-primary btn-sm rounded-pill"
                                >
                                  <i className="ri-external-link-line me-1"></i>
                                  View Video
                                </a>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="small text-muted">
                          <div className="d-flex justify-content-between mb-1">
                            <span><i className="ri-user-line me-1"></i>Created by:</span>
                            <span className="fw-semibold text-truncate" style={{ maxWidth: '120px' }}>
                              {media.createdBy.fullName || "Unknown"}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span><i className="ri-mail-line me-1"></i>For:</span>
                            <span className="fw-semibold text-truncate" style={{ maxWidth: '120px' }}>
                              {media.createdFor}
                            </span>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </PerfectScrollbar>
          </Card.Body>
        </Card>
      </div>
    </React.Fragment>
  );
}
